import { computed, ref } from 'vue'
import { SPECIES_IDS } from '@/lib/species'

// Marine Discovery — brugerens fundne arter.
//
// Samlingen hoerer til kontoen, saa den overlever et enhedsskift. Men en bruger der ikke er logget
// ind, skal ikke miste sit foerste fund: fund lander da i localStorage og sendes samlet ved foerste
// login, hvor serveren tager unionen. Uden det ville systemet foeles uretfaerdigt praecis der, hvor
// man lige har opdaget at det findes.
//
// Module-level ref som useUnits: delt tilstand, laest under render, saa visningen foelger med.

const STORAGE_KEY = 'discoveries'

function fraLager(): string[] {
  try {
    const raa = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
    return Array.isArray(raa) ? raa.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function tilLager(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // Privat browsing kan afvise skrivning. Et tabt paaskeaeg er ikke vaerd at kaste op paa.
  }
}

const discovered = ref<string[]>(fraLager())
const loaded = ref(false)
/** Sidste fund — driver den diskrete toast. */
const justFound = ref<string | null>(null)

const found = computed(() => discovered.value.length)
const total = computed(() => SPECIES_IDS.length)
const has = (id: string) => discovered.value.includes(id)

async function post(ids: string[]): Promise<string[] | null> {
  try {
    const res = await fetch('/api/user/discoveries', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ speciesIds: ids }),
    })
    if (!res.ok) return null
    return ((await res.json()) as { speciesIds: string[] }).speciesIds
  } catch {
    return null
  }
}

/**
 * Hentes ved opstart naar brugeren er logget ind. Har enheden lokale fund fra dengang brugeren var
 * anonym, sendes de med samme kald — serveren svarer med unionen, som saa bliver den nye sandhed.
 */
async function load(isAuthenticated: boolean): Promise<void> {
  if (loaded.value) return
  loaded.value = true
  if (!isAuthenticated) return

  const lokale = fraLager()
  const svar = lokale.length > 0
    ? await post(lokale)
    : await (async () => {
      try {
        const res = await fetch('/api/user/discoveries', { headers: { Accept: 'application/json' } })
        if (!res.ok) return null
        return ((await res.json()) as { speciesIds: string[] }).speciesIds
      } catch {
        return null
      }
    })()

  if (svar) {
    discovered.value = svar
    // Lageret ryddes ikke: er man logget ud igen, er samlingen stadig synlig paa enheden.
    tilLager(svar)
  }
}

/** Registrér et fund. Stille no-op hvis arten allerede er fundet. */
async function discover(id: string, isAuthenticated: boolean): Promise<void> {
  if (has(id)) return

  discovered.value = [...discovered.value, id]
  tilLager(discovered.value)
  justFound.value = id

  if (isAuthenticated) {
    const svar = await post([id])
    if (svar) {
      discovered.value = svar
      tilLager(svar)
    }
  }
}

function dismissToast(): void {
  justFound.value = null
}

export function useDiscoveries() {
  return { discovered, found, total, has, discover, load, justFound, dismissToast }
}
